import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Grid } from '@react-three/drei';
import { Suspense, useRef, useState, useCallback } from 'react';
import {
  Box3,
  Vector3,
  PerspectiveCamera as ThreePerspectiveCamera,
} from 'three';
import { Bug, SlidersHorizontal } from 'lucide-react';
import { PuppetModel, type PuppetModelRef } from './PuppetModel';
import { ModelLoader } from './ModelLoader';
import { CameraController } from './CameraController';
import { DebugPanel } from './DebugPanel';
import { PositionControlPanel } from './PositionControlPanel';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useSceneStore } from '../../stores/sceneStore';
import { usePuppetStore } from '../../stores/puppetStore';
import type { AnimationState } from './hooks/useAnimationUpdate';
import type { MotorPositions } from '../../stores/puppetStore';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import { applyPositions } from '../../utils/animation';

/**
 * DoubleClickHandler Component
 *
 * Handles double-click to fit model in view.
 * Calculates bounding box of the puppet model and adjusts camera to frame it.
 */
function DoubleClickHandler({
  puppetRef,
  controlsRef,
}: {
  puppetRef: React.RefObject<PuppetModelRef | null>;
  controlsRef: React.RefObject<OrbitControlsImpl | null>;
}) {
  const { camera } = useThree();

  const fitToView = useCallback(() => {
    const group = puppetRef.current?.group;
    const controls = controlsRef.current;

    if (!group || !controls) return;

    // Calculate bounding box of the model
    const box = new Box3().setFromObject(group);
    const center = box.getCenter(new Vector3());
    const size = box.getSize(new Vector3());

    // Get the maximum dimension for framing
    const maxDim = Math.max(size.x, size.y, size.z);

    // Calculate ideal distance based on camera FOV
    const fov = (camera as ThreePerspectiveCamera).fov * (Math.PI / 180);
    const distance = (maxDim / (2 * Math.tan(fov / 2))) * 1.5; // 1.5x for padding

    // Clamp distance to OrbitControls limits
    const clampedDistance = Math.max(2, Math.min(20, distance));

    // Calculate new camera position (front view, slightly elevated)
    const newPosition = new Vector3(
      center.x,
      center.y + size.y * 0.1, // Slightly above center
      center.z + clampedDistance
    );

    // Animate to new position
    camera.position.copy(newPosition);
    controls.target.copy(center);
    controls.update();
  }, [camera, puppetRef, controlsRef]);

  return (
    <mesh visible={false} position={[0, 1, 0]} onDoubleClick={fitToView}>
      {/* Invisible click target covering typical puppet area */}
      <boxGeometry args={[4, 4, 4]} />
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
  );
}

/**
 * Canvas3DPreview Component
 *
 * Main 3D preview canvas using React Three Fiber.
 * Provides camera controls, lighting, and grid floor for puppet animation preview.
 *
 * Features:
 * - Dark background (#1a1a1a) matching application theme
 * - PerspectiveCamera positioned at (0, 1.5, 5) for front-facing medium distance view
 * - OrbitControls: left-click rotate, scroll zoom, right-click pan
 * - Double-click to center and fit model in view
 * - Grid floor helper for spatial reference
 * - Ambient and directional lighting for depth perception
 * - PuppetModel component with GLTF loading and error handling
 * - ModelLoader for loading custom models per tab
 * - Camera reset on tab switch with smooth transitions
 * - Loading state with spinner (Task 10.6)
 * - Debug panel for visualizing animation state (Task 3.0)
 */
export function Canvas3DPreview() {
  const puppetRef = useRef<PuppetModelRef>(null);
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const [modelError, setModelError] = useState<string | null>(null);

  // Debug panel state (Task 3.2)
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  // Position control panel state
  const [showPositionPanel, setShowPositionPanel] = useState(false);

  // Animation state for debug panel - will be updated via callback from PuppetModel
  const [animationState, setAnimationState] = useState<AnimationState | null>(
    null
  );

  // Get active tab and its model path from sceneStore
  const activeTabId = useSceneStore((state) => state.activeTabId);
  const activeTab = useSceneStore((state) =>
    state.tabs.find((t) => t.id === state.activeTabId)
  );
  const modelPath = activeTab?.modelPath || '/models/default-puppet.glb';

  // NOTE: Removed timeline subscriptions (playheadPosition, tools, isPlaying)
  // These were causing the entire 3D canvas to re-render 60x/second during playback!
  // DebugPanel now gets these values directly via getState() when needed.

  const handleModelError = (error: Error) => {
    console.error('Model loading error:', error);
    setModelError(error.message);
  };

  const handleModelLoad = () => {
    setModelError(null);
  };

  // Callback to receive animation state from PuppetModel (Task 3.4)
  const handleAnimationStateUpdate = useCallback(
    (state: AnimationState) => {
      // Update state when debug panel OR position panel is visible
      if (showDebugPanel || showPositionPanel) {
        setAnimationState(state);
      }
    },
    [showDebugPanel, showPositionPanel]
  );

  // Toggle debug panel visibility (Task 3.3)
  const toggleDebugPanel = useCallback(() => {
    setShowDebugPanel((prev) => !prev);
  }, []);

  // Toggle position control panel
  const togglePositionPanel = useCallback(() => {
    setShowPositionPanel((prev) => !prev);
  }, []);

  // Get motor config for position application
  const getMotorConfig = usePuppetStore((state) => state.getMotorConfig);

  // Handle position change from control panel - apply to 3D model AND animation state
  const handlePositionChange = useCallback(
    (positions: MotorPositions) => {
      // Apply positions to the 3D model immediately
      if (puppetRef.current?.skeleton) {
        applyPositions(puppetRef.current, positions, (bodyPart) => {
          const config = getMotorConfig(bodyPart);
          return {
            axis: config?.axis ?? 'x',
            boneOffset: config?.boneOffset ?? 0,
            direction: config?.direction ?? 1,
            xOffset: config?.xOffset ?? 0,
            yOffset: config?.yOffset ?? 0,
            zOffset: config?.zOffset ?? 0,
          };
        });

        // Also update animation state so it knows the new "current position"
        puppetRef.current.setAnimationState(positions);
      }
    },
    [getMotorConfig]
  );

  return (
    <div className="relative h-full w-full">
      <Canvas
        style={{ background: '#1a1a1a' }}
        gl={{ antialias: true }}
        dpr={[1, 2]} // Device pixel ratio for better quality on high-DPI screens
      >
        {/* Camera Setup */}
        <PerspectiveCamera
          makeDefault
          position={[0, 1.5, 5]}
          fov={50}
          near={0.1}
          far={1000}
        />

        {/* Camera Controls */}
        <OrbitControls
          ref={controlsRef}
          enableDamping
          dampingFactor={0.05}
          minDistance={2}
          maxDistance={20}
          maxPolarAngle={Math.PI / 2} // Prevent camera from going below ground
          mouseButtons={{
            LEFT: 0, // ROTATE - orbit around target
            MIDDLE: 1, // DOLLY - zoom via middle mouse
            RIGHT: 2, // PAN - move camera target
          }}
        />

        {/* Camera Controller - handles tab switch camera reset */}
        <CameraController controlsRef={controlsRef} />

        {/* Lighting */}
        <ambientLight intensity={0.5} />
        <directionalLight
          position={[5, 5, 5]}
          intensity={0.8}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <directionalLight position={[-5, 3, -5]} intensity={0.3} />

        {/* Grid Floor Helper */}
        <Grid
          args={[10, 10]} // Grid size: 10x10 units
          cellSize={0.5}
          cellThickness={0.5}
          cellColor="#404040"
          sectionSize={1}
          sectionThickness={1}
          sectionColor="#525252"
          fadeDistance={30}
          fadeStrength={1}
          followCamera={false}
          infiniteGrid={false}
        />

        {/* Puppet Model with Suspense for loading states (Task 10.6) */}
        <Suspense
          fallback={
            <mesh position={[0, 1, 0]}>
              <boxGeometry args={[1, 2, 0.5]} />
              <meshStandardMaterial color="#3b82f6" wireframe />
            </mesh>
          }
        >
          <PuppetModel
            ref={puppetRef}
            modelPath={modelPath}
            onError={handleModelError}
            onLoad={handleModelLoad}
            onAnimationStateUpdate={handleAnimationStateUpdate}
          />
        </Suspense>

        {/* Double-click handler for fit-to-view */}
        <DoubleClickHandler puppetRef={puppetRef} controlsRef={controlsRef} />
      </Canvas>

      {/* Loading Overlay - Centered (Task 10.6) */}
      <Suspense
        fallback={
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <LoadingSpinner size={48} message="Loading 3D model..." />
          </div>
        }
      >
        {/* Empty div to trigger Suspense fallback during initial load */}
        <div />
      </Suspense>

      {/* Top Right Controls - Model Loader, Position Panel, and Debug Toggle */}
      {activeTabId && (
        <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
          <ModelLoader tabId={activeTabId} />
          <button
            onClick={togglePositionPanel}
            className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
              showPositionPanel
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
            aria-label="Toggle Position Controls"
            title="Toggle Position Controls"
          >
            <SlidersHorizontal className="h-4 w-4" />
          </button>
          <button
            onClick={toggleDebugPanel}
            className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
              showDebugPanel
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
            aria-label="Toggle Debug Panel"
            title="Toggle Debug Panel"
          >
            <Bug className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Debug Panel Overlay - Bottom Left (Task 3.5) */}
      {showDebugPanel && (
        <div className="absolute bottom-4 left-4 z-10">
          <DebugPanel
            visible={showDebugPanel}
            animationState={animationState}
          />
        </div>
      )}

      {/* Position Control Panel Overlay - Bottom Right */}
      {showPositionPanel && (
        <div className="absolute bottom-4 right-4 z-10">
          <PositionControlPanel
            visible={showPositionPanel}
            onPositionChange={handlePositionChange}
            currentPositions={animationState?.currentRotations}
          />
        </div>
      )}

      {/* Error Display Overlay - Top Left */}
      {modelError && (
        <div className="absolute left-4 top-4 max-w-md rounded-md border border-red-500 bg-red-950/90 p-4 text-sm text-red-200">
          <h3 className="mb-2 font-semibold text-red-100">Model Load Error</h3>
          <p>{modelError}</p>
          <p className="mt-2 text-xs text-red-300">
            Please ensure the model file exists and has a valid skeleton
            structure.
          </p>
        </div>
      )}
    </div>
  );
}
